import { createClient } from '@supabase/supabase-js';

// Falls back to a syntactically valid placeholder so the app can still render
// (with empty data) before a real Supabase project is connected via .env —
// a real "YOUR_SUPABASE_URL" string is not a valid URL and crashes createClient().
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'placeholder-anon-key';

export const supabase = createClient(supabaseUrl, supabaseKey);

// ─── Products ─────────────────────────────────────────────────────────────────
// Merges in units_sold from the product_sales_counts view -- best-effort,
// silently skipped if the view doesn't exist yet (older DB not migrated).
async function attachSalesCounts(products) {
  if (!products || products.length === 0) return products;
  const { data: counts } = await getProductSalesCounts();
  if (!counts) return products;
  const map = Object.fromEntries(counts.map(c => [c.product_id, c.units_sold]));
  return products.map(p => ({ ...p, units_sold: map[p.id] || 0 }));
}

export const getProducts = async (category = null) => {
  let query = supabase.from('products').select('*, categories(name, slug)').eq('is_active', true);
  if (category) query = query.eq('categories.slug', category);
  const { data, error } = await query.order('created_at', { ascending: false });
  return { data: await attachSalesCounts(data), error };
};

export const getProductById = async (id) => {
  const { data, error } = await supabase.from('products').select('*, categories(name, slug)').eq('id', id).single();
  if (!data) return { data, error };
  const [withCounts] = await attachSalesCounts([data]);
  return { data: withCounts, error };
};

// Fetches a specific set of products by id, in the given order -- used by
// the admin-curated homepage showcase (distinct from is_featured, which
// drives the separate "Bestsellers" grid).
export const getProductsByIds = async (ids) => {
  if (!ids || ids.length === 0) return { data: [], error: null };
  const { data, error } = await supabase.from('products').select('*, categories(name, slug)').in('id', ids).eq('is_active', true);
  if (!data) return { data, error };
  const withCounts = await attachSalesCounts(data);
  const ordered = ids.map(id => withCounts.find(p => p.id === id)).filter(Boolean);
  return { data: ordered, error };
};

export const getFeaturedProducts = async () => {
  const { data, error } = await supabase.from('products').select('*, categories(name, slug)').eq('is_featured', true).eq('is_active', true).limit(8);
  return { data: await attachSalesCounts(data), error };
};

// ─── Categories ───────────────────────────────────────────────────────────────
export const getCategories = async () => {
  const { data, error } = await supabase.from('categories').select('*').eq('is_active', true).order('sort_order');
  return { data, error };
};

export const upsertCategory = async (category) => {
  const { data, error } = await supabase.from('categories').upsert([category]).select().single();
  return { data, error };
};

export const deleteCategory = async (id) => {
  const { data, error } = await supabase.from('categories').delete().eq('id', id);
  return { data, error };
};

// ─── Orders ───────────────────────────────────────────────────────────────────
export const createOrder = async (orderData) => {
  const { data, error } = await supabase.from('orders').insert([orderData]).select().single();
  return { data, error };
};

export const getOrderById = async (id) => {
  const { data, error } = await supabase.from('orders').select('*, order_items(*, products(name, image_url))').eq('id', id).single();
  return { data, error };
};

export const createOrderItems = async (orderId, items) => {
  const rows = items.map(item => ({
    order_id: orderId,
    product_id: item.product_id ?? item.id,
    name: item.name,
    price: item.price,
    quantity: item.quantity,
  }));
  const { data, error } = await supabase.from('order_items').insert(rows).select();
  return { data, error };
};

export const createShiprocketShipment = async (orderId, pkg) => {
  const { data, error } = await supabase.functions.invoke('shiprocket-create-shipment', {
    body: { order_id: orderId, ...pkg },
  });
  return { data, error };
};

export const updateOrderDelivery = async (id, { courier_name, tracking_number }) => {
  const { data, error } = await supabase.from('orders')
    .update({ courier_name, tracking_number, updated_at: new Date().toISOString() })
    .eq('id', id);
  return { data, error };
};

// ─── Checkout (verified payment) ───────────────────────────────────────────────
export const createRazorpayOrder = async (items, paymentMethod, delivery_pincode, courier_id, customer) => {
  const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
    body: { items, paymentMethod, delivery_pincode, courier_id, customer },
  });
  return { data, error };
};

// Live courier options + prices for a delivery pincode. Weight is computed
// server-side from each product's real billed weight -- the client only
// sends item ids/quantities and the destination pincode.
export const getShippingRates = async (items, delivery_pincode) => {
  const { data, error } = await supabase.functions.invoke('get-shipping-rates', {
    body: { items, delivery_pincode },
  });
  return { data, error };
};

export const verifyRazorpayPayment = async (payload) => {
  const { data, error } = await supabase.functions.invoke('verify-razorpay-payment', {
    body: payload,
  });
  return { data, error };
};

// ─── Custom Orders ────────────────────────────────────────────────────────────
export const submitCustomOrder = async (formData) => {
  const { data, error } = await supabase.from('custom_orders').insert([formData]).select().single();
  return { data, error };
};

// Uploads a single reference image via the upload-custom-order-image Edge
// Function (service role) -- the anon key alone has no storage write access.
export const uploadCustomOrderImage = async (blob) => {
  try {
    const formData = new FormData();
    formData.append('file', blob, 'reference.jpg');
    const { data, error } = await supabase.functions.invoke('upload-custom-order-image', { body: formData });
    if (error || !data?.url) return { url: null, error: error || new Error('Upload failed') };
    return { url: data.url, error: null };
  } catch (err) {
    return { url: null, error: err };
  }
};

// Best-effort admin alert (email/WhatsApp) for a new custom order lead.
// Never throws -- a failed alert shouldn't block the "request received" UI.
export const notifyCustomOrder = async (formData) => {
  try {
    await supabase.functions.invoke('notify-custom-order', { body: formData });
  } catch (err) {
    console.error('notifyCustomOrder failed:', err);
  }
};

export const getCustomOrders = async () => {
  const { data, error } = await supabase.from('custom_orders').select('*').order('created_at', { ascending: false });
  return { data, error };
};

export const updateCustomOrderStatus = async (id, status) => {
  const { data, error } = await supabase.from('custom_orders').update({ status }).eq('id', id);
  return { data, error };
};

// ─── Admin ────────────────────────────────────────────────────────────────────
export const getAllOrders = async () => {
  const { data, error } = await supabase.from('orders').select('*, order_items(*, products(name))').order('created_at', { ascending: false });
  return { data, error };
};

export const updateOrderStatus = async (id, status) => {
  const { data, error } = await supabase.from('orders').update({ status, updated_at: new Date().toISOString() }).eq('id', id);
  return { data, error };
};

export const upsertProduct = async (product) => {
  const { data, error } = await supabase.from('products').upsert([product]).select().single();
  return { data, error };
};

export const deleteProduct = async (id) => {
  const { data, error } = await supabase.from('products').update({ is_active: false }).eq('id', id);
  return { data, error };
};

// Routed through an Edge Function (service role) rather than a direct
// client upload -- this project's Storage service currently rejects the
// newer JWT signing-key format directly, so admin uploads go through a
// server-verified path instead. See admin-manage-product-image.
export const uploadProductImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const { data, error } = await supabase.functions.invoke('admin-manage-product-image', { body: formData });
  if (error || !data?.url) return { url: null, error: error || new Error('Upload failed') };
  return { url: data.url, error: null };
};

// ─── Order tracking (public, no auth) ──────────────────────────────────────────
export const trackOrder = async (order_number, contact) => {
  try {
    const { data, error } = await supabase.functions.invoke('track-order', { body: { order_number, contact } });
    if (error || !data?.data) return { data: null, error: error || new Error(data?.error || 'Order not found') };
    return { data: data.data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
};

// ─── Newsletter ───────────────────────────────────────────────────────────────
export const subscribeNewsletter = async (email) => {
  try {
    const { data, error } = await supabase.functions.invoke('subscribe-newsletter', { body: { email } });
    if (error) return { ok: false, configured: true, error };
    return { ok: data?.ok, configured: data?.configured !== false, error: null };
  } catch (err) {
    return { ok: false, configured: true, error: err };
  }
};

// ─── Popularity ───────────────────────────────────────────────────────────────
// Reads the product_sales_counts view (units sold per product, paid orders
// only) -- a narrow aggregate, not the raw orders/order_items tables.
export const getProductSalesCounts = async () => {
  const { data, error } = await supabase.from('product_sales_counts').select('*');
  return { data, error };
};

// ─── Reviews ──────────────────────────────────────────────────────────────────
export const getApprovedReviews = async (productId) => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('product_id', productId)
    .eq('is_approved', true)
    .order('created_at', { ascending: false });
  return { data, error };
};

export const submitReview = async (review) => {
  const { data, error } = await supabase.from('reviews').insert([review]).select().single();
  return { data, error };
};

export const getAllReviews = async () => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*, products(name)')
    .order('created_at', { ascending: false });
  return { data, error };
};

export const approveReview = async (id) => {
  const { data, error } = await supabase.from('reviews').update({ is_approved: true }).eq('id', id);
  return { data, error };
};

// Admin-only: flip a published review back to pending without deleting it.
export const unapproveReview = async (id) => {
  const { data, error } = await supabase.from('reviews').update({ is_approved: false }).eq('id', id);
  return { data, error };
};

// Admin-only: correct a review's wording, rating or author name.
export const updateReview = async (id, fields) => {
  const { data, error } = await supabase.from('reviews').update(fields).eq('id', id).select().single();
  return { data, error };
};

// Admin-only: write a review directly, for feedback that arrived by WhatsApp
// or in person rather than through the site form.
export const createReview = async (review) => {
  const { data, error } = await supabase
    .from('reviews')
    .insert([{ ...review, is_approved: true }])
    .select('*, products(name)')
    .single();
  return { data, error };
};

export const deleteReview = async (id) => {
  const { data, error } = await supabase.from('reviews').delete().eq('id', id);
  return { data, error };
};

// ─── Homepage Content ───────────────────────────────────────────────────────────
export const getHomepageContent = async () => {
  const { data, error } = await supabase.from('homepage_content').select('*');
  if (error) return { data: null, error };
  const bySection = {};
  (data || []).forEach(row => { bySection[row.section] = row.content; });
  return { data: bySection, error: null };
};

export const updateHomepageSection = async (section, content) => {
  const { data, error } = await supabase
    .from('homepage_content')
    .upsert([{ section, content, updated_at: new Date().toISOString() }], { onConflict: 'section' })
    .select().single();
  return { data, error };
};
